// Full-board undo/redo over a widget board whose source of truth lives in the
// CONSUMER's store. This hook never owns the board: it records snapshots of
// the committed `board` prop and hands a restored board back through `commit`
// — ONE op the consumer routes to its own persistence (whatever staging,
// forking, or write-through semantics that store implements).
//
// Wiring:
//   - `handleLayoutChange` → the grid's full-layout `onLayoutChange`. It
//     RECORDS only — the grid commit already flows to persistence through the
//     grid's `onMove`, so committing here too would double-persist.
//   - `snapshot()` → call immediately BEFORE a board-membership op the host
//     initiates (add, duplicate, replace, delete, seed). Records the
//     pre-op board unconditionally.
//   - `apply(nextLayout)` → programmatic placement changes (tidy/pack):
//     records, then commits the placements through the consumer.
//   - `undo`/`redo` → restore a snapshot via `commit`, rebuilt against the
//     live board by `restoreBoardSnapshot` (config edits survive; membership
//     and placements roll back).
//
// The stacks reset when `resetKey` changes — a different key is a different
// board, and its snapshots must not leak across. Pair with
// `useGridHistoryHotkeys` for the keyboard bindings.

import * as React from "react";

import type { Omittable } from "../../lib/props";
import type { GridLayoutItem } from "../sortable-grid";
import type { WidgetInstance } from "./types";
import {
  applyPlacements,
  restoreBoardSnapshot,
  samePlacements,
} from "./widget-board-history";

const DEFAULT_LIMIT = 50;

export interface WidgetBoardHistory {
  /** Wire to the grid's `onLayoutChange`. Records the pre-change board
   *  (skipping no-op gestures); does NOT commit. */
  handleLayoutChange: (next: GridLayoutItem[]) => void;
  /** Record the current board before a host-initiated membership op
   *  (add/duplicate/replace/delete/seed). */
  snapshot: () => void;
  /** Record the current board, then commit `next`'s placements — for
   *  programmatic layout changes (Tidy) that don't flow through a gesture. */
  apply: (next: GridLayoutItem[]) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useWidgetBoardHistory(
  options: Omittable<{
    /** The current committed board (store truth). */
    board: WidgetInstance[];
    /** The consumer's persistence path for a restored/applied board. */
    commit: (next: WidgetInstance[]) => void;
    /** Clears the stacks when it changes — pass the board's identity key. */
    resetKey: string;
    /** Max undo depth. Defaults to 50. */
    limit?: number;
  }>,
): WidgetBoardHistory {
  const { board, commit, resetKey, limit = DEFAULT_LIMIT } = options;

  const boardRef = React.useRef(board);
  const commitRef = React.useRef(commit);
  React.useLayoutEffect(() => {
    boardRef.current = board;
    commitRef.current = commit;
  });

  const pastRef = React.useRef<WidgetInstance[][]>([]);
  const futureRef = React.useRef<WidgetInstance[][]>([]);
  const [flags, setFlags] = React.useState({ canUndo: false, canRedo: false });

  // Render-phase reset on key change, mirroring a store's own re-seed — a new
  // board must never restore another board's snapshot.
  const resetKeyRef = React.useRef(resetKey);
  if (resetKey !== resetKeyRef.current) {
    resetKeyRef.current = resetKey;
    pastRef.current = [];
    futureRef.current = [];
    setFlags((prev) => (prev.canUndo || prev.canRedo ? { canUndo: false, canRedo: false } : prev));
  }

  const syncFlags = React.useCallback(() => {
    const next = {
      canUndo: pastRef.current.length > 0,
      canRedo: futureRef.current.length > 0,
    };
    setFlags((prev) =>
      prev.canUndo === next.canUndo && prev.canRedo === next.canRedo ? prev : next,
    );
  }, []);

  const snapshot = React.useCallback(() => {
    pastRef.current.push(boardRef.current.map((i) => ({ ...i })));
    if (pastRef.current.length > limit) pastRef.current.shift();
    futureRef.current = [];
    syncFlags();
  }, [limit, syncFlags]);

  const handleLayoutChange = React.useCallback(
    (next: GridLayoutItem[]) => {
      // A commit that changed nothing (e.g. a drag returned to its origin)
      // must not burn an undo step.
      if (samePlacements(boardRef.current, next)) return;
      snapshot();
    },
    [snapshot],
  );

  const apply = React.useCallback(
    (next: GridLayoutItem[]) => {
      if (samePlacements(boardRef.current, next)) return;
      const restored = applyPlacements(boardRef.current, next);
      snapshot();
      commitRef.current(restored);
    },
    [snapshot],
  );

  const undo = React.useCallback(() => {
    const snap = pastRef.current.pop();
    if (!snap) return;
    futureRef.current.push(boardRef.current.map((i) => ({ ...i })));
    syncFlags();
    commitRef.current(restoreBoardSnapshot(snap, boardRef.current));
  }, [syncFlags]);

  const redo = React.useCallback(() => {
    const snap = futureRef.current.pop();
    if (!snap) return;
    pastRef.current.push(boardRef.current.map((i) => ({ ...i })));
    syncFlags();
    commitRef.current(restoreBoardSnapshot(snap, boardRef.current));
  }, [syncFlags]);

  return React.useMemo(
    () => ({
      handleLayoutChange,
      snapshot,
      apply,
      undo,
      redo,
      canUndo: flags.canUndo,
      canRedo: flags.canRedo,
    }),
    [handleLayoutChange, snapshot, apply, undo, redo, flags.canUndo, flags.canRedo],
  );
}
